import nanoid from 'nanoid';

/**
 * Components store
 * @property {Map<string, Component>} byId Inner store
 */
class Store {
  constructor() {
    this.byId = new Map();
  }

}

/**
 * Inner service for wrap document's methods (create elements, text nodes & comments)
 */
class Dom {
  /**
   * Construct dom enabled
   * @param {Component} component
   * @return {boolean}
   */
  //eslint-disable-next-line no-unused-vars
  build(component) {
    return true;
  }
  /**
   * Create element
   * @param {Component} component
   * @param {string} tagName
   * @return {HTMLElement}
   */


  el(component, tagName) {
    return document.createElement(tagName);
  }
  /**
   * Create a text node
   * @param {Component} component
   * @param {string} data
   * @return {Text}
   */


  text(component, data) {
    return document.createTextNode(data);
  }
  /**
   * Create comment node
   * @param {Component} component
   * @param {string} data
   * @return {Comment}
   */


  comment(component, data) {
    return document.createComment(data);
  }
  /**
   * This function is being used for unsafe `innerHTML` insertion of HTML into DOM.
   * Code looks strange. I know. But it is possible minimalistic implementation of.
   *
   * @param {Comment} component
   * @param root {Element} Node there to insert unsafe html.
   * @param nodes {Array} List of already inserted html nodes for remove.
   * @param html {string} Unsafe html to insert.
   */
  // eslint-disable-next-line no-unused-vars


  unsafe(component, root, nodes, html) {
    throw new Error('Require sham-ui-unsafe');
  }

}

/**
 * Inner service for hydrate/rehydrate hooks
 */

class Hooks {
  /**
   * Hook for extra processing after component created
   * @param {Component} component
   */
  //eslint-disable-next-line no-unused-vars
  create(component) {}
  /**
   * Hook for mark root component as ready for hydrating
   * @param {Component} component
   */
  //eslint-disable-next-line no-unused-vars


  hydrate(component) {}
  /**
   * Hook for mark rehydrating component
   * @param {Component} component
   */
  //eslint-disable-next-line no-unused-vars


  rehydrate(component) {}
  /**
   * Hook for resolve ID for component
   * @param {Component} component
   * @return {string}
   */


  resolveID(component) {
    const ID = component.ctx.ID;
    return 'string' === typeof ID ? ID : nanoid();
  }

}

/**
 * @inner
 * @param {DI} DI
 * @return {DI}
 */

function bindServices(DI) {
  return DI.bindLazy('sham-ui:store', Store).bindLazy('sham-ui:dom', Dom).bindLazy('sham-ui:hooks', Hooks);
}

/**
 * Create new instance of DI
 * @return {DI}
 */

function createDI() {
  /**
   * Bindings map
   * @type {Map}
   * @inner
   */
  const bindings = new Map();
  /**
   * Factories
   * @type {Map}
   * @inner
   */

  const factories = new Map();
  return bindServices(
  /**
   * @class DI
   */
  {
    /**
     * Bind item by name
     * @memberof DI
     * @param {string} name
     * @param {*} item
     * @return {DI}
     */
    bind(name, item) {
      bindings.set(name, item);
      return this;
    },

    /**
     * Lazy bind item factory by name
     * @memberof DI
     * @param {string} name
     * @param {*} factory
     * @return {DI}
     */
    bindLazy(name, factory) {
      factories.set(name, factory);
      return this;
    },

    /**
     * Get item from container by name
     * @memberof DI
     * @param {string} name
     * @return {*}
     */
    resolve(name) {
      // If name already resolved
      if (bindings.has(name)) {
        return bindings.get(name);
      } // Lazy resolve


      if (factories.has(name)) {
        // Create
        const item = new (factories.get(name))(); // Save

        bindings.set(name, item); // Remove from lazy factories mapping

        factories.delete(name);
        return item;
      }
    }

  });
}

/**
 * @inner
 * @param {Object} object
 * @param {String} name
 * @param {*} value
 * @return {Object}
 */
function setupDescriptor(object, name, value) {
  // eslint-disable-next-line no-nested-ternary
  const descriptor = 'object' === typeof value && null !== value ? // Value is valid descriptor?
  'get' in value || 'set' in value ? // Then, use value as descriptor
  value : // Else, wrap value to get
  {
    get() {
      return value;
    }

  } : // Value is not object, wrap it
  {
    value
  };
  Object.defineProperty(object, name, Object.assign({}, descriptor, {
    configurable: true,
    enumerable: true
  }));
}
/**
 * @param {Object} current
 * @param {Object} newOptions
 * @param {Function} proxy
 * @return {Object}
 */


function setDefaultOptions(current, newOptions, proxy) {
  for (const name in newOptions) {
    if (!(name in current)) {
      setupDescriptor(current, name, newOptions[name]); // Set property getter/setter in proxy

      setupDescriptor(proxy, name, {
        get: () => current[name],
        set: value => proxy({
          [name]: value
        })
      });
    }
  }

  return current;
}

/**
 * Wrap service methods for pass component as first argument
 * @param {string} serviceName
 * @param {string[]}methods
 * @param {Component} component
 * @inner
 */
function createProxy(serviceName, methods, component) {
  const proxy = {};
  methods.forEach(name => proxy[name] = function () {
    const service = component.ctx.DI.resolve(serviceName);
    return service[name].apply(service, // Pass component to service method as first argument
    [].concat(component, Array.from(arguments)));
  });
  return proxy;
}

/**
 * Create proxy methods for pass current component
 * @param {Component} component
 * @inner
 */

function setupForComponent(component) {
  component.dom = createProxy('sham-ui:dom', ['build', 'el', 'text', 'comment', 'unsafe'], component);
  component.hooks = createProxy('sham-ui:hooks', ['create', 'rehydrate', 'resolveID'], component);
}

/**
 * Bit mask for spot
 * @inner
 * @typedef {number} SpotBitMask
 */

/**
 * Add variable to cache
 * @inner
 * @type {SpotBitMask}
 */

const SPOT_CACHE = 1 << 0;
/**
 * Spot dependent on data.__index__
 * @inner
 * @type {SpotBitMask}
 */

const SPOT_LOOP = 1 << 1;
/**
 * Spot definition from template compiler
 * Spot it's dynamic part of template: {{a}}, {{a + b}}, variables in {% if %}, {% for %} etc
 * @inner
 * @typedef {Array} Spot
 * @property {SpotBitMask} 0 Spot options
 * @property {Array<string>|string} 1 Variables for spots
 * @property {Function|undefined} 2 Operation for update spot
 */

/**
 * Setup default component state
 * Return reference to component state
 * @callback optionsCallback
 * @param {Object.<string, *>} defaultState
 * @return {Object.<string, *>}
 */

/**
 * Registry function on component didMount hook.
 * Param callback will call after component render & update
 * @callback didMountCallback
 * @param {Function} callback
 * @return {onRemoveCallback|undefined}
 */

/**
 * Registry function on component onRemove hook
 * Param callback will call after component remove
 * @callback onRemoveCallback
 * @param {Function} callback
 */

/**
 * Registry function on component didReceive hook.
 * Param callback will call after component receive new state update from outer components
 * @callback didReceiveCallback
 * @param {Function} callback
 */

/**
 * Constructor function for component
 * @callback componentConstructor
 * @param {optionsCallback} options
 * @param {didMountCallback} didMount
 * @param {didReceiveCallback} didReceive
 * @this {Component}
 */

/**
 * Factory for create new component
 * @param {...componentConstructor} [constructors]
 * @return {Component.constructor}
 */

function ComponentFactory(...constructors) {
  /**
   * Base component class
   * @param {Object} context Context
   * @param {Object} [options] Default options
   * @property {Object} ctx Context
   * @property {string} ID Component unique ID
   * @property {Dom} dom
   * @property {Store} UI
   * @property {Hooks} hooks
   */
  return class Component {
    constructor(context, options = {}) {
      this.ctx = context;
      setupForComponent(this); // Create proxy function

      const stateProxy = newState => this.updateState(newState);

      this.options = setDefaultOptions({}, options, stateProxy);
      this.UI = this.ctx.DI.resolve('sham-ui:store');
      this.ID = this.hooks.resolveID();
      /**
       * @inner
       * @type {Array<Spot>}
       */

      this.spots = [];
      /**
       * @type {Array<Component>} Array of child components
       */

      this.nested = [];
      /**
       * @type {Array<Element>} Component elements
       */

      this.nodes = [];
      /**
       * @inner
       * @type {Array<Function>}
       */

      this.onMount = [];
      /**
       * @inner
       * @type {Array<Function>}
       */

      this.onRemove = [];
      /**
       * @inner
       * @type {Array<Function>}
       */

      this.onReceive = []; // Set inner props:

      this.__cache__ = {};
      this.isRoot = false;
      this.UI.byId.set(this.ID, this); // Call all constructors passed to factory

      constructors.forEach(constructor => constructor.call(this, // Callback "options" for pass to constructor
      options => {
        setDefaultOptions(this.options, options || {}, stateProxy);
        return stateProxy;
      }, // Callback "didMount" for pass to constructor
      callback => this.onMount.push(callback), // Callback "didReceive" for pass to constructor
      callback => this.onReceive.push(callback))); // Call hook for extra processing

      this.hooks.create();
    }
    /**
     * Hook for extra processing after render & update
     */


    didMount() {
      // Call callbacks from factory
      this.onMount.forEach(callback => {
        const onRemove = callback(); // If didMount return function, then registry that as onRemove callback

        if (onRemove) {
          this.onRemove.push(onRemove);
        }
      });
    }
    /**
     * Add new spot
     * @inner
     * @param {...Spot} spots
     */


    addSpots(...spots) {
      this.spots.push(...spots);
    }
    /**
     * Update component state & call hooks
     * @param {Object}  currentData
     */


    update(currentData) {
      this.updateState(currentData); // Call callbacks from factory

      this.onReceive.forEach(callback => callback(currentData));
    }
    /**
     * Update component state
     * @inner
     * @param {Object}  currentData
     */


    updateState(currentData) {
      const data = Object.assign({}, this.options, currentData);
      this.__data__ = data; // Process spots provided by compiler or manual added

      for (let spot of this.spots) {
        // bitMask must be last
        const last = spot[spot.length - 1];

        this._updateSpot(data, [].concat(spot[0]), spot[1], 'number' === typeof last ? last : 0);
      } // Call on update callback.


      if (this.onUpdate) {
        this.onUpdate(data);
      }

      if (currentData) {
        Object.defineProperties(this.options, Object.getOwnPropertyDescriptors(currentData));
      }

      delete this.__data__;
    }
    /**
     * Update one spot
     * @param {Object} data
     * @param {Array<string>} variables
     * @param {*} operation
     * @param {SpotBitMask} bitMask
     * @private
     */


    _updateSpot(data, variables, operation, bitMask) {
      let params = [];

      if (variables.length > 1) {
        // Spot dependents on several variables: {{a + b}} for example
        params = variables.map(variableName => this.__cache__[variableName]);
      } else {
        // Spot dependent on one or zero variable: {{foo}} for example
        const name = variables[0];
        const value = variables.length === 1 ? data[name] : null;

        if (undefined === value || // Data hasn't value
        bitMask & SPOT_LOOP && undefined === data.__index__ // Variable from loop, but loop not created
        ) {
            return;
          }

        if (bitMask & SPOT_CACHE) {
          // Caching variable
          this.__cache__[name] = value;
        }

        params = [value];
      }

      if (operation && operation.apply) {
        operation.apply(this, params);
      }
    }
    /**
     * Helpers for blocks.
     * Use because this.__data__ can be undefined (before update or after update finished)
     * @param {Object} blockData
     * @return {any}
     * @private
     */


    _dataForBlock(blockData) {
      const ownerData = this.__data__ || Object.assign({}, this.options);
      return Object.assign(blockData, ownerData);
    }
    /**
     * Mount component to container element
     */


    render() {
      if (this.dom.build()) {
        const node = this.ctx.container; // COMMENT_NODE

        if (node.nodeType === 8) {
          const parentNode = node.parentNode;

          for (let i = 0, len = this.nodes.length; i < len; i++) {
            parentNode.insertBefore(this.nodes[i], node);
          }
        } else {
          for (let i = 0, len = this.nodes.length; i < len; i++) {
            node.appendChild(this.nodes[i]);
          }
        }
      } // Call on render callback.


      if (this.onRender) {
        this.onRender();
      }
    }
    /**
     * Remove & destroy component
     */


    remove() {
      this.UI.byId.delete(this.ID); // Remove appended nodes.

      let i = this.nodes.length;

      while (i--) {
        this.nodes[i].parentNode.removeChild(this.nodes[i]);
      } // Remove all nested views.


      i = this.nested.length;

      while (i--) {
        this.nested[i].remove();
      } // Remove this view from parent's nested views.


      const parent = this.ctx.parent;

      if (parent) {
        parent.nested.splice(parent.nested.indexOf(this), 1);
      } // Call callbacks from factory


      this.onRemove.forEach(callback => callback());
      this.ctx.resetOwner();
      this.ctx = null;
    }

  };
}

/**
 * @inner
 * @param {Object} context Context
 * @param {Class<Component>} template Component class for insert, if test true
 * @param {Function} afterInstance
 * @param {Object} [data]
 */
function createComponent(context, template, afterInstance, data) {
  // Render new view.
  const view = new template(context); // Resolve owner

  if (!context.owner) {
    context.owner = view.isRoot ? view : context.parent.ctx.owner;
  }

  view.render(); // Set view hierarchy.

  context.parent.nested.push(view); // Process extra after instance handler

  afterInstance(view, context); // Rehydrate component

  view.hooks.rehydrate();

  if (data) {
    // Set view data (note what it must be after adding nodes to DOM).
    view.update(data);
  } // Call hook


  view.didMount();
}
/**
 * @inner
 * @param {Component} view
 * @param {Object} context
 */

function saveRef(view, context) {
  // Remember to remove child ref on remove of view.
  context.ref = view;
  view.onRemove.push(() => context.ref = null);
}

/**
 * Custom tags processor.
 * @param {Object} context Context
 * @param {Class<Component>} template Component class for insert, if test true
 * @param {Object} data Options for component
 */

function insert(context, template, data) {
  if (context.ref) {
    // If view was already inserted, update or remove it.
    context.ref.update(data);
  } else {
    createComponent(context, template, saveRef, data);
  }
}

/**
 * If condition processor.
 * @param {Object} context Context
 * @param {Class<Component>} template Component class for insert, if test true
 * @param {boolean} test Condition test
 * @return {boolean} test result
 */

function cond(context, template, test) {
  if (context.ref) {
    // If view was already inserted, update or remove it.
    if (!test) {
      context.ref.remove();
    }
  } else if (test) {
    createComponent(context, template, saveRef);
  }

  return test;
}

/**
 * @inner
 * @typedef {Object} LoopOptions
 * @property {String} value
 * @property {String} key
 */

/**
 * @inner
 * @param {number} index
 * @return {number}
 */

const transformArrayKey = index => index;
/**
 * @inner
 * @param {string[]} keys
 * @return {function(number): string}
 */


const transformObjectKey = keys => index => keys[index];
/**
 * Build loopItem state
 * @inner
 * @param {Object|Array}array
 * @param {String|Number} key
 * @param {number} index
 * @param {LoopOptions|null} options
 * @return {*}
 */


function buildState(array, key, index, options) {
  if (options) {
    const item = {
      __index__: index
    };
    item[options.value] = array[key];

    if (options.key) {
      item[options.key] = key;
    }

    return item;
  }

  return array[key];
}
/**
 * Loops processor
 * @param {Object} context Context
 * @param {Class<Component>} template Component class for insert, if test true
 * @param {Array|Object} array Iterated object or array
 * @param {LoopOptions|null} options Options for component
 */


function loop(context, template, array, options) {
  let transformKey, arrayLength; // Get array length, and convert object to array if needed.

  if (Array.isArray(array)) {
    transformKey = transformArrayKey;
    arrayLength = array.length;
  } else {
    const keys = Object.keys(array);
    transformKey = transformObjectKey(keys);
    arrayLength = keys.length;
  }

  const loopItems = context.ref;
  const childrenSize = loopItems.length; // If new array contains less items what before, remove surpluses.

  let len = childrenSize - arrayLength;

  for (let i in loopItems.items) {
    if (len-- > 0) {
      loopItems.items[i].remove();
    } else {
      break;
    }
  } // If there is already some views, update there loop state.


  let j = 0;

  for (let i in loopItems.items) {
    loopItems.items[i].__state__ = buildState(array, transformKey(j), j, options);
    j++;
  } // If new array contains more items when previous, render new views and append them.


  for (let j = childrenSize; j < arrayLength; j++) {
    createComponent(context.extend({
      parent: context.parent,
      container: context.container
    }), template, view => {
      // Remember to remove from children loopItems on view remove.
      const i = loopItems.push(view);
      view.onRemove.push(() => loopItems.remove(i)); // Set view state for later update in onUpdate.

      view.__state__ = buildState(array, transformKey(j), j, options);
    });
  }
}

/**
 * @inner
 * @return {{DI: *, directives: *, filters: *, appendDirectives: appendDirectives, appendFilters: appendFilters}}
 */
function extend(extra) {
  return Object.assign({
    DI: this.DI,
    directives: this.directives,
    filters: this.filters,
    blocks: this.blocks || [],

    appendDirectives(newDirectives) {
      this.directives = Object.assign({}, this.directives, newDirectives);
    },

    appendFilters(newFilters) {
      this.filters = Object.assign({}, this.filters, newFilters);
    },

    onUpdate(data) {
      if (this.ref) {
        this.ref.updateState(data);
      }
    },

    resetOwner() {
      this.owner = null;
    },

    extend,
    ref: null
  }, extra);
}

/**
 * @param {Object} $0
 * @param {DI} $0.DI
 * @param {string} [$0.ID]
 * @param {Element} $0.container
 * @param {Object} [$0.directives={}]
 * @param {Object} [$0.filters=[]]
 * @return {Object}
 */

function createRootContext({
  DI,
  ID,
  container,
  directives = {},
  filters = {}
}) {
  return extend.call({
    DI,
    directives,
    filters
  }, {
    ID,
    container
  });
}

/**
 * @param {Component} parent
 * @param {Element} container
 * @param {Object} blocks
 * @return {Object}
 */
function createChildContext(parent, container, blocks) {
  const extra = {
    parent,
    container
  };

  if (blocks) {
    extra.blocks = blocks;
  }

  return parent.ctx.extend(extra);
}

/**
 * @param {Component} parent
 * @param {Element} container
 * @return {*}
 */
function createLoopContext(parent, container) {
  return parent.ctx.extend({
    parent,
    container,
    ref: loopItems()
  });
}
/**
 * Simple Map implementation with length property.
 * @inner
 */

function loopItems() {
  const items = Object.create(null);
  let next = 0;
  return {
    items,
    length: 0,

    push(element) {
      items[next] = element;
      this.length += 1;
      next += 1;
      return next - 1;
    },

    remove(i) {
      if (i in items) {
        delete items[i];
        this.length -= 1;
      }
    },

    updateState(data) {
      for (let i in items) {
        const item = items[i];
        item.updateState(Object.assign({}, data, item.__state__));
      }
    }

  };
}

/**
 * @param {Component} owner
 * @return {Object}
 */
function createBlockContext(owner) {
  return owner.ctx.extend({
    resetOwner() {},

    setup(parent, container) {
      return Object.assign(this, {
        parent,
        container,
        // Resolve owner before insert block component, because in call from
        // component constructor owner may be no resolved
        owner: owner.isRoot ? owner : owner.ctx.owner
      });
    }

  });
}

/**
 * Render root components
 * @param {DI} DI
 */

function start(DI) {
  const hooks = DI.resolve('sham-ui:hooks');
  Array.from( // Get root components
  DI.resolve('sham-ui:store').byId.values()).forEach(component => {
    // Save owner of root component in context
    component.ctx.owner = component; // Mount to dom

    component.render(); // Rehydrate component

    component.hooks.rehydrate(); // Call updateState (for inner call onUpdate)

    component.updateState(); // Call hook

    component.didMount(); // Hydrate root component

    hooks.hydrate(component);
  });
}

export { ComponentFactory as Component, cond, createBlockContext, createChildContext, createDI, createLoopContext, createRootContext, insert, loop, start };
